/**
 * AddToBucketListButton — 1-tap save of a place or trail stop to the UAE
 * Bucket List. Works for guests (saved on this device, moved to their
 * account on sign-in) and signed-in users.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { addToBucketList, bucketTitleForPlace } from "@/lib/bucketList";
import { cn } from "@/lib/utils";

interface Props {
  name: string;
  area?: string | null;
  className?: string;
  variant?: "default" | "outline" | "secondary";
  compact?: boolean;
}

export function AddToBucketListButton({ name, area, className, variant = "secondary", compact }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");

  const handleClick = async () => {
    if (state !== "idle") return;
    setState("saving");
    const result = await addToBucketList(bucketTitleForPlace(name, area), user?.id);
    if (result === "error") {
      setState("idle");
      toast({ title: "Couldn't save right now", description: "Please try again in a moment.", variant: "destructive" });
      return;
    }
    setState("saved");
    toast({
      title: result === "exists" ? "Already on your Bucket List" : "Saved to your Bucket List ✨",
      description: user
        ? "Find it under My Goals → Bucket List."
        : "Saved on this device. Sign in anytime to keep it on all your devices.",
      action: user ? undefined : (
        <Button size="sm" variant="outline" onClick={() => navigate(`/auth?next=${encodeURIComponent(window.location.pathname)}`)}>
          Sign in
        </Button>
      ) as any,
    });
  };

  const saved = state === "saved";
  return (
    <Button
      type="button"
      variant={variant}
      onClick={handleClick}
      aria-label={saved ? `${name} is on your bucket list` : `Add ${name} to your bucket list`}
      className={cn("h-11 rounded-2xl touch-manipulation gd-spring transition-all", saved && "ring-1 ring-inset ring-primary/20", className)}
      disabled={state === "saving"}
    >
      {state === "saving" ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : saved ? (
        <BookmarkCheck className="h-4 w-4" />
      ) : (
        <Bookmark className="h-4 w-4" />
      )}
      {!compact && <span className="ml-1.5">{saved ? "Saved to my list ✓" : "Save – I want to go"}</span>}
    </Button>
  );
}
